import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiAbsoluteUrl, apiGet, apiPatch, apiPost } from "@/lib/apiClient";
import { formatFinanceMoney } from "@/lib/financeFormat";
import {
  INVOICE_PAYMENT_TERMS,
  computeLineAmounts,
  dueDateFromTerms,
  paymentTermsLabel,
} from "@/lib/financeInvoiceLineMath";
import { financeInvoiceBalanceDue, financeInvoiceTotals } from "@/lib/financeInvoiceTotals";
import { useFinanceAccountPaths, useFinanceInvoicePaths } from "@/lib/financeRouteContext";
import type { FinanceCustomer, FinanceInvoice, FinanceInvoiceStatus, FinancePayment } from "@/lib/financeTypes";
import { FinanceErrorBanner, FinanceStatusBadge, financeStatusIsOpen } from "@/lib/financeUi";
import { ArrowLeft, FileText, Plus } from "lucide-react";

type LineDraft = {
  key: string;
  description: string;
  quantity: string;
  unitPrice: string;
};

type CustomersResponse = { customers?: FinanceCustomer[] };
type PaymentsResponse = { payments?: FinancePayment[] };

const STATUSES: FinanceInvoiceStatus[] = ["draft", "sent", "partial", "paid", "void"];

function newLine(partial?: Partial<LineDraft>): LineDraft {
  return {
    key: `line_${Math.random().toString(36).slice(2, 10)}`,
    description: partial?.description ?? "",
    quantity: partial?.quantity ?? "1",
    unitPrice: partial?.unitPrice ?? "0",
  };
}

function lineAmount(line: Omit<LineDraft, "key" | "description">): number {
  return computeLineAmounts({
    quantity: Number(line.quantity) || 0,
    unitPrice: Number(line.unitPrice) || 0,
  }).amount;
}

function linesFromInvoice(inv: FinanceInvoice): LineDraft[] {
  if (!Array.isArray(inv.lines) || inv.lines.length === 0) {
    return [newLine()];
  }
  return inv.lines.map((line) =>
    newLine({
      description: line.description || "",
      quantity: String(line.quantity ?? 1),
      unitPrice: String(line.unitPrice ?? 0),
    }),
  );
}

function resolveSendToPreview(inv: FinanceInvoice, customer: FinanceCustomer | null): string | null {
  let to = inv.billToEmail?.trim().toLowerCase() || null;
  if (!to && inv.billToUseAccountEmail !== false) {
    to = customer?.email?.trim().toLowerCase() || null;
  }
  if (!to) {
    to = customer?.email?.trim().toLowerCase() || null;
  }
  return to;
}

function toApiLine(l: LineDraft) {
  const quantity = Number(l.quantity) || 0;
  const unitPrice = Number(l.unitPrice) || 0;
  return {
    description: l.description.trim() || "Line",
    quantity,
    unitPrice,
    amount: lineAmount(l),
  };
}

export function FinanceInvoiceDetailPage() {
  const { listPath, paymentsPath } = useFinanceInvoicePaths();
  const { detailPath: accountDetailPath } = useFinanceAccountPaths();
  const { invoiceId: idParam } = useParams<{ invoiceId: string }>();
  const invoiceId = idParam ? decodeURIComponent(idParam) : "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendNote, setSendNote] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<FinanceInvoice | null>(null);
  const [payments, setPayments] = useState<FinancePayment[]>([]);
  const [customers, setCustomers] = useState<FinanceCustomer[]>([]);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<FinanceInvoiceStatus>("draft");
  const [number, setNumber] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("custom");
  const [dueDate, setDueDate] = useState("");
  const [dueDateManual, setDueDateManual] = useState(true);
  const [orderNumber, setOrderNumber] = useState("");
  const [subject, setSubject] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [editLines, setEditLines] = useState<LineDraft[]>([newLine()]);
  const [amountOnly, setAmountOnly] = useState("");

  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalCopied, setPortalCopied] = useState(false);

  const linkedCustomer = useMemo(
    () => customers.find((c) => c.id === invoice?.customerId) ?? null,
    [customers, invoice?.customerId],
  );

  const sendToPreview = useMemo(
    () => (invoice ? resolveSendToPreview(invoice, linkedCustomer) : null),
    [invoice, linkedCustomer],
  );

  const load = useCallback(async () => {
    if (!invoiceId) return;
    setError(null);
    setLoading(true);
    const [invRes, payRes, custRes] = await Promise.all([
      apiGet<FinanceInvoice>(`/api/finance/invoices/${encodeURIComponent(invoiceId)}`),
      apiGet<PaymentsResponse>(
        `/api/finance/payments?invoiceId=${encodeURIComponent(invoiceId)}&limit=100`,
      ),
      apiGet<CustomersResponse>("/api/finance/customers?limit=200"),
    ]);
    if (!invRes.ok) {
      setError(invRes.error || "Could not load invoice.");
      setInvoice(null);
      setPayments([]);
    } else {
      const inv = invRes.data;
      setInvoice(inv);
      setPayments(payRes.ok ? (payRes.data?.payments ?? []) : []);
      hydrateEditFields(inv!);
    }
    if (custRes.ok) setCustomers(custRes.data?.customers ?? []);
    setLoading(false);
  }, [invoiceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const hydrateEditFields = (inv: FinanceInvoice) => {
    setStatus((inv.status as FinanceInvoiceStatus) || "draft");
    setNumber(inv.number || "");
    setCustomerId(inv.customerId || "");
    setIssueDate(inv.issueDate || "");
    setPaymentTerms(inv.paymentTerms || "custom");
    setDueDate(inv.dueDate || "");
    setDueDateManual(true);
    setOrderNumber(inv.orderNumber || "");
    setSubject(inv.subject || "");
    setCustomerNotes(inv.customerNotes || "");
    setTermsAndConditions(inv.termsAndConditions || "");
    setEditLines(linesFromInvoice(inv));
    setAmountOnly(String(inv.amount ?? ""));
  };

  const balanceDue = invoice ? financeInvoiceBalanceDue(invoice) : 0;
  const canRecordPayment =
    !!invoice && invoice.status !== "void" && invoice.status !== "paid" && balanceDue > 0;
  const canShowPayPortal = canRecordPayment && invoice?.status !== "draft";
  const canSend =
    invoice?.status === "draft" || invoice?.status === "sent" || invoice?.status === "partial";
  const canMarkSent = invoice?.status === "draft";

  const loadPayPortalUrl = useCallback(async () => {
    if (!invoiceId || !canShowPayPortal) {
      setPortalUrl(null);
      return;
    }
    setPortalLoading(true);
    const statusRes = await apiGet<{ stripeConfigured?: boolean }>(
      `/api/finance/invoices/${encodeURIComponent(invoiceId)}/checkout`,
    );
    if (!statusRes.ok || !statusRes.data?.stripeConfigured) {
      setPortalUrl(null);
      setPortalLoading(false);
      return;
    }
    const payRes = await apiPost<{ portalUrl?: string }>(
      `/api/finance/invoices/${encodeURIComponent(invoiceId)}/pay`,
      {},
    );
    setPortalUrl(payRes.ok && payRes.data?.portalUrl ? payRes.data.portalUrl : null);
    setPortalLoading(false);
  }, [invoiceId, canShowPayPortal]);

  useEffect(() => {
    void loadPayPortalUrl();
  }, [loadPayPortalUrl]);

  useEffect(() => {
    if (!editing || dueDateManual || paymentTerms === "custom") return;
    const next = dueDateFromTerms(issueDate, paymentTerms);
    if (next) setDueDate(next);
  }, [editing, issueDate, paymentTerms, dueDateManual]);

  const updateEditLine = (key: string, patch: Partial<LineDraft>) => {
    setEditLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const onSave = async () => {
    if (!invoice?.id) return;
    setSaving(true);
    setError(null);
    const selected = customers.find((c) => c.id === customerId);
    const builtLines = editLines.map(toApiLine).filter((l) => l.description || l.amount > 0 || l.unitPrice > 0);
    const body: Record<string, unknown> = {
      status,
      number: number.trim() || invoice.number,
      customerId: customerId || null,
      customerName: selected?.name?.trim() || invoice.customerName || null,
      issueDate: issueDate || null,
      paymentTerms: paymentTerms || null,
      dueDate: dueDate || null,
      orderNumber: orderNumber.trim() || null,
      subject: subject.trim() || null,
      customerNotes: customerNotes.trim() || null,
      termsAndConditions: termsAndConditions.trim() || null,
    };
    if (builtLines.length > 0) {
      body.lines = builtLines;
      body.amount = builtLines.reduce((s, l) => s + l.amount, 0);
    } else {
      body.lines = [];
      body.amount = amountOnly ? Number(amountOnly) : 0;
    }
    const res = await apiPatch<FinanceInvoice>(
      `/api/finance/invoices/${encodeURIComponent(invoice.id)}`,
      body,
    );
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed.");
      return;
    }
    setInvoice(res.data);
    setEditing(false);
    await load();
  };

  const onSendInvoice = async (markSentOnly: boolean) => {
    if (!invoice?.id) return;
    if (!markSentOnly) {
      if (!sendToPreview) {
        setError(
          "A bill-to email is required to send this invoice. Set the account email or bill-to on the invoice.",
        );
        return;
      }
      const ok = window.confirm(
        `Email invoice ${invoice.number} to ${sendToPreview}? Messages go to that address only.`,
      );
      if (!ok) return;
    }
    setSending(true);
    setError(null);
    setSendNote(null);
    const res = await apiPost<{
      invoice?: FinanceInvoice;
      emailed?: boolean;
      emailSkipped?: boolean;
      to?: string | null;
      attachmentFilename?: string;
      messageId?: string;
    }>(`/api/finance/invoices/${encodeURIComponent(invoice.id)}/send`, { markSentOnly });
    setSending(false);
    if (!res.ok) {
      setError(res.error || "Send failed.");
      return;
    }
    if (res.data?.invoice) setInvoice(res.data.invoice);
    if (!markSentOnly && res.data?.emailSkipped) {
      setError(
        "Invoice marked as Sent. Email was skipped because outbound email is not configured in this environment.",
      );
    } else if (!markSentOnly && res.data?.emailed) {
      const toLabel = res.data.to || sendToPreview || "the customer";
      const sesRef = res.data.messageId ? ` Reference: ${res.data.messageId}.` : "";
      setSendNote(
        `Invoice emailed to ${toLabel} with ${res.data.attachmentFilename || "the invoice PDF"} attached.${sesRef}`,
      );
    }
    await load();
  };

  const copyPortalLink = async () => {
    if (!portalUrl) return;
    try {
      await navigator.clipboard.writeText(portalUrl);
      setPortalCopied(true);
      window.setTimeout(() => setPortalCopied(false), 2000);
    } catch {
      setError("Could not copy the pay link.");
    }
  };

  const pdfUrl = invoice?.id
    ? apiAbsoluteUrl(`/api/finance/invoices/${encodeURIComponent(invoice.id)}/pdf`)
    : null;

  const totals = invoice ? financeInvoiceTotals(invoice) : null;
  const currency = invoice?.currency || "USD";
  const accountHref = invoice?.customerId ? accountDetailPath(invoice.customerId) : null;

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <Link
        to={listPath}
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All invoices
      </Link>

      {loading ? <p className="text-sm text-slate-500">Loading invoice…</p> : null}
      {error ? <FinanceErrorBanner message={error} /> : null}
      {sendNote ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-100">
          {sendNote}
        </div>
      ) : null}

      {invoice && !loading ? (
        <>
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-950/40">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <FileText className="h-8 w-8 text-indigo-600" aria-hidden />
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Invoice</p>
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {invoice.number || invoice.id}
                  </h1>
                  {accountHref ? (
                    <Link to={accountHref} className="text-sm text-indigo-600 hover:underline dark:text-indigo-300">
                      {invoice.customerName?.trim() || "View account"}
                    </Link>
                  ) : (
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {invoice.customerName?.trim() || "No account name"}
                    </p>
                  )}
                  <div className="mt-1">
                    <FinanceStatusBadge status={invoice.status} />
                  </div>
                </div>
              </div>
              <div className="flex max-w-xl flex-col items-end gap-2">
                <div className="flex flex-wrap justify-end gap-2">
                  {pdfUrl ? (
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                    >
                      Download PDF
                    </a>
                  ) : null}
                  {canSend ? (
                    <button
                      type="button"
                      onClick={() => void onSendInvoice(false)}
                      disabled={sending}
                      className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                    >
                      {sending ? "Sending…" : invoice.status === "draft" ? "Send invoice" : "Resend invoice"}
                    </button>
                  ) : null}
                  {canMarkSent ? (
                    <button
                      type="button"
                      onClick={() => void onSendInvoice(true)}
                      disabled={sending}
                      className="rounded-md border border-slate-300 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:bg-slate-900 dark:text-slate-200"
                    >
                      Mark as sent
                    </button>
                  ) : null}
                  {!editing ? (
                    <button
                      type="button"
                      onClick={() => {
                        hydrateEditFields(invoice);
                        setEditing(true);
                      }}
                      className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                    >
                      Edit
                    </button>
                  ) : null}
                  {canShowPayPortal && portalUrl ? (
                    <>
                      <a
                        href={portalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500"
                      >
                        Open pay portal
                      </a>
                      <button
                        type="button"
                        onClick={() => void copyPortalLink()}
                        className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                      >
                        {portalCopied ? "Link copied" : "Copy pay link"}
                      </button>
                    </>
                  ) : canShowPayPortal && portalLoading ? (
                    <span className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-500">
                      Pay link…
                    </span>
                  ) : null}
                  {canRecordPayment ? (
                    <Link
                      to={`${paymentsPath}?invoiceId=${encodeURIComponent(invoice.id)}`}
                      className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500"
                    >
                      Record payment
                    </Link>
                  ) : null}
                </div>
                {canSend && sendToPreview ? (
                  <p className="text-xs text-slate-500">
                    Sends to <span className="font-medium text-slate-700 dark:text-slate-300">{sendToPreview}</span>
                  </p>
                ) : null}
              </div>
            </div>

            {editing ? (
              <div className="mt-6 space-y-6 border-t border-slate-200 pt-6 dark:border-white/10">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Edit invoice</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Invoice number</span>
                    <input
                      value={number}
                      onChange={(e) => setNumber(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Status</span>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as FinanceInvoiceStatus)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm sm:col-span-2">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Account</span>
                    <select
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                    >
                      <option value="">No linked account</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name?.trim() || c.id}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Order number</span>
                    <input
                      value={orderNumber}
                      onChange={(e) => setOrderNumber(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Issue date</span>
                    <input
                      type="date"
                      value={issueDate}
                      onChange={(e) => setIssueDate(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Terms</span>
                    <select
                      value={paymentTerms}
                      onChange={(e) => {
                        setPaymentTerms(e.target.value);
                        setDueDateManual(e.target.value === "custom");
                      }}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                    >
                      {INVOICE_PAYMENT_TERMS.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Due date</span>
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => {
                        setDueDate(e.target.value);
                        setDueDateManual(true);
                        if (paymentTerms !== "custom") setPaymentTerms("custom");
                      }}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                    />
                  </label>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Line items</h3>
                    <button
                      type="button"
                      onClick={() => setEditLines((prev) => [...prev, newLine()])}
                      className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 dark:text-indigo-300"
                    >
                      <Plus className="h-4 w-4" aria-hidden />
                      Add row
                    </button>
                  </div>
                  <div className="divide-y divide-slate-100 rounded-md border border-slate-200 dark:divide-white/5 dark:border-white/10">
                    {editLines.map((line) => (
                      <div key={line.key} className="grid gap-2 p-3 sm:grid-cols-12 sm:items-end">
                        <label className="block text-sm sm:col-span-5">
                          <span className="font-medium text-slate-700 dark:text-slate-300">Description</span>
                          <input
                            value={line.description}
                            onChange={(e) => updateEditLine(line.key, { description: e.target.value })}
                            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                          />
                        </label>
                        <label className="block text-sm sm:col-span-2">
                          <span className="font-medium text-slate-700 dark:text-slate-300">Qty</span>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={line.quantity}
                            onChange={(e) => updateEditLine(line.key, { quantity: e.target.value })}
                            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                          />
                        </label>
                        <label className="block text-sm sm:col-span-2">
                          <span className="font-medium text-slate-700 dark:text-slate-300">Rate</span>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={line.unitPrice}
                            onChange={(e) => updateEditLine(line.key, { unitPrice: e.target.value })}
                            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                          />
                        </label>
                        <div className="text-sm sm:col-span-2">
                          <span className="font-medium text-slate-700 dark:text-slate-300">Amount</span>
                          <p className="mt-2 tabular-nums">{formatFinanceMoney(lineAmount(line), currency)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setEditLines((prev) =>
                              prev.length <= 1 ? [newLine()] : prev.filter((l) => l.key !== line.key),
                            )
                          }
                          className="text-sm text-rose-600 sm:col-span-1"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  {editLines.every((l) => !l.description.trim() && !l.unitPrice.trim()) ? (
                    <label className="mt-3 block text-sm">
                      <span className="font-medium text-slate-700 dark:text-slate-300">Amount only ({currency})</span>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={amountOnly}
                        onChange={(e) => setAmountOnly(e.target.value)}
                        className="mt-1 w-full max-w-xs rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                      />
                    </label>
                  ) : null}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm sm:col-span-2">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Subject</span>
                    <textarea
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      rows={2}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                    />
                  </label>
                  <label className="block text-sm sm:col-span-2">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Account notes</span>
                    <textarea
                      value={customerNotes}
                      onChange={(e) => setCustomerNotes(e.target.value)}
                      rows={3}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                    />
                  </label>
                  <label className="block text-sm sm:col-span-2">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Terms and conditions</span>
                    <textarea
                      value={termsAndConditions}
                      onChange={(e) => setTermsAndConditions(e.target.value)}
                      rows={3}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                    />
                  </label>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void onSave()}
                    disabled={saving}
                    className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      if (invoice) hydrateEditFields(invoice);
                    }}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="mt-6 grid gap-4 lg:grid-cols-2">
                  <div className="rounded-md border border-slate-100 p-4 dark:border-white/5">
                    <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Account and addresses</h2>
                    <div className="mt-3 space-y-3 text-sm">
                      <div>
                        <p className="text-xs font-semibold uppercase text-slate-500">Bill-to / send email</p>
                        <p className="mt-1 font-medium text-slate-900 dark:text-white">
                          {sendToPreview || invoice.billToEmail?.trim() || "—"}
                        </p>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <p className="text-xs font-semibold uppercase text-slate-500">Billing</p>
                          <pre className="mt-1 whitespace-pre-wrap font-sans text-slate-700 dark:text-slate-300">
                            {invoice.billingAddressText?.trim() || "—"}
                          </pre>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase text-slate-500">Shipping</p>
                          <pre className="mt-1 whitespace-pre-wrap font-sans text-slate-700 dark:text-slate-300">
                            {invoice.shippingAddressText?.trim() || "—"}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </div>
                  {totals ? (
                    <div className="rounded-md border border-slate-100 p-4 dark:border-white/5">
                      <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Totals</h2>
                      <p className="text-xs text-slate-500">Currency {currency}</p>
                      <dl className="mt-3 space-y-1 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-slate-500">Subtotal</dt>
                          <dd className="tabular-nums">{formatFinanceMoney(totals.subtotal, currency)}</dd>
                        </div>
                        {totals.discountAmount > 0 ? (
                          <div className="flex justify-between">
                            <dt className="text-slate-500">{totals.discountLabel}</dt>
                            <dd className="tabular-nums">-{formatFinanceMoney(totals.discountAmount, currency)}</dd>
                          </div>
                        ) : null}
                        {totals.shippingCharges > 0 ? (
                          <div className="flex justify-between">
                            <dt className="text-slate-500">Shipping</dt>
                            <dd className="tabular-nums">{formatFinanceMoney(totals.shippingCharges, currency)}</dd>
                          </div>
                        ) : null}
                        {totals.lateFee > 0 ? (
                          <div className="flex justify-between">
                            <dt className="text-slate-500">Late fee</dt>
                            <dd className="tabular-nums">{formatFinanceMoney(totals.lateFee, currency)}</dd>
                          </div>
                        ) : null}
                        <div className="flex justify-between">
                          <dt className="text-slate-500">
                            Tax{totals.taxRate > 0 ? ` (${totals.taxRate}%)` : ""}
                          </dt>
                          <dd className="tabular-nums">{formatFinanceMoney(totals.taxAmount, currency)}</dd>
                        </div>
                        <div className="flex justify-between font-semibold">
                          <dt className="text-slate-700 dark:text-slate-300">Total</dt>
                          <dd className="tabular-nums">{formatFinanceMoney(totals.total, currency)}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-slate-500">Paid</dt>
                          <dd className="tabular-nums">
                            {formatFinanceMoney(Number(invoice.amountPaid) || 0, currency)}
                          </dd>
                        </div>
                        <div className="flex justify-between font-semibold">
                          <dt className="text-slate-700 dark:text-slate-300">Balance</dt>
                          <dd className="tabular-nums">{formatFinanceMoney(balanceDue, currency)}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-slate-500">Terms</dt>
                          <dd>{paymentTermsLabel(invoice.paymentTerms)}</dd>
                        </div>
                        {invoice.orderNumber ? (
                          <div className="flex justify-between">
                            <dt className="text-slate-500">Order number</dt>
                            <dd>{invoice.orderNumber}</dd>
                          </div>
                        ) : null}
                      </dl>
                    </div>
                  ) : null}
                </div>

                {(invoice.customerNotes?.trim() || invoice.termsAndConditions?.trim()) && (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {invoice.customerNotes?.trim() ? (
                      <div className="rounded-md border border-slate-100 p-4 dark:border-white/5">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Account notes</h3>
                        <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-400">
                          {invoice.customerNotes}
                        </p>
                      </div>
                    ) : null}
                    {invoice.termsAndConditions?.trim() ? (
                      <div className="rounded-md border border-slate-100 p-4 dark:border-white/5">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Terms and conditions</h3>
                        <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-400">
                          {invoice.termsAndConditions}
                        </p>
                      </div>
                    ) : null}
                  </div>
                )}

                <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    { label: "Issue", value: invoice.issueDate || "—" },
                    { label: "Due", value: invoice.dueDate || "—" },
                    { label: "Subject", value: invoice.subject?.trim() || "—" },
                  ].map((row) => (
                    <div key={row.label} className={row.label === "Subject" ? "sm:col-span-2 lg:col-span-4" : undefined}>
                      <dt className="text-xs font-semibold uppercase text-slate-500">{row.label}</dt>
                      <dd className="mt-1 text-sm font-medium text-slate-900 dark:text-white">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </>
            )}
          </div>

          {!editing && invoice.lines && invoice.lines.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
              <div className="border-b border-slate-200 px-4 py-3 dark:border-white/10">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Line items</h2>
              </div>
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Description</th>
                    <th className="px-4 py-3 font-semibold">Qty</th>
                    <th className="px-4 py-3 font-semibold">Unit</th>
                    <th className="px-4 py-3 font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lines.map((line, i) => (
                    <tr key={i} className="border-b border-slate-100 dark:border-white/5">
                      <td className="px-4 py-3 text-slate-900 dark:text-white">{line.description || "—"}</td>
                      <td className="px-4 py-3 tabular-nums">{line.quantity}</td>
                      <td className="px-4 py-3 tabular-nums">
                        {formatFinanceMoney(line.unitPrice, currency)}
                      </td>
                      <td className="px-4 py-3 tabular-nums">{formatFinanceMoney(line.amount, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-white/10">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Payments received</h2>
              <Link
                to={paymentsPath}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
              >
                All payments
              </Link>
            </div>
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Method</th>
                  <th className="px-4 py-3 font-semibold">Reference</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                      No payments yet.
                      {financeStatusIsOpen(invoice.status) && balanceDue > 0 ? (
                        <>
                          {" "}
                          <Link
                            to={`${paymentsPath}?invoiceId=${encodeURIComponent(invoice.id)}`}
                            className="text-indigo-600 hover:underline dark:text-indigo-300"
                          >
                            Record payment
                          </Link>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ) : (
                  payments.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 dark:border-white/5">
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{p.paymentDate || "—"}</td>
                      <td className="px-4 py-3 tabular-nums">
                        {formatFinanceMoney(p.amount, p.currency || currency)}
                      </td>
                      <td className="px-4 py-3 capitalize text-slate-600 dark:text-slate-300">
                        {p.method || "other"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{p.reference || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
